import type { ContentfulClientApi, EntryFieldTypes, EntrySkeletonType } from "contentful";

import { getLogger } from "@expert/logging";

import type { Language } from "../types";

const logger = getLogger({ module: "getSalesNarrativeContent", supportTeam: "sales" });

/** One piece of approved copy. `key` is the stable handle that becomes the narrative beat id. */
export interface SalesNarrativeContentItem {
    key: string;
    text: string;
}

/**
 * The approved copy for one SKU, as named stages of the call. Structure (steps, beats, ordering) is
 * deliberately not modelled in the CMS — the consumer assembles it.
 */
export interface SalesNarrativeContent {
    productSku: string;
    transition: SalesNarrativeContentItem;
    /** Absent for offers that are not walked through on-screen. */
    guide?: SalesNarrativeContentItem;
    offerBenefits: SalesNarrativeContentItem[];
    close: SalesNarrativeContentItem;
    enroll: SalesNarrativeContentItem;
}

interface ItemFields {
    key: EntryFieldTypes.Symbol;
    text: EntryFieldTypes.Text;
}
type ItemSkeleton = EntrySkeletonType<ItemFields, "salesNarrativeStaticContentItem">;

interface ContentFields {
    productSku: EntryFieldTypes.Symbol;
    transition: EntryFieldTypes.EntryLink<ItemSkeleton>;
    guide?: EntryFieldTypes.EntryLink<ItemSkeleton>;
    offerBenefits: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<ItemSkeleton>>;
    close: EntryFieldTypes.EntryLink<ItemSkeleton>;
    enroll: EntryFieldTypes.EntryLink<ItemSkeleton>;
}

type LinkedItem = { fields?: { key?: string; text?: string } } | undefined;

const toItem = (linked: LinkedItem): SalesNarrativeContentItem | undefined => {
    const key = linked?.fields?.key;
    const text = linked?.fields?.text;
    return key && text ? { key, text } : undefined;
};

/**
 * Fetch the approved narrative copy for a SKU.
 *
 * Returns `null` when the entry is missing or incomplete rather than a partial narrative — a narrative
 * missing its close or its enrollment ask is worse than none, because the expert would read most of a
 * pitch and hit a dead end. `withoutUnresolvableLinks` is what makes an unpublished linked item show
 * up as absent instead of throwing.
 */
export async function getSalesNarrativeContent(
    client: ContentfulClientApi<undefined>,
    productSku: string,
    language: Language,
): Promise<SalesNarrativeContent | null> {
    try {
        const response = await client.withoutUnresolvableLinks.getEntries<EntrySkeletonType<ContentFields>>({
            content_type: "salesNarrativeStaticContent",
            "fields.productSku": productSku,
            locale: language,
            include: 2,
        });

        const entry = response.items[0];
        if (!entry) {
            // Warn, not silence: an absent entry is the likeliest production failure (the entry never
            // promoted to this space/locale), and the incomplete-entry warn below does not cover it.
            logger.warn({ productSku, language }, "No sales narrative content entry found");
            return null;
        }

        const transition = toItem(entry.fields.transition as LinkedItem);
        const close = toItem(entry.fields.close as LinkedItem);
        const enroll = toItem(entry.fields.enroll as LinkedItem);
        const offerBenefits = ((entry.fields.offerBenefits ?? []) as LinkedItem[])
            .map(toItem)
            .filter((item): item is SalesNarrativeContentItem => item !== undefined);

        if (!transition || !close || !enroll || offerBenefits.length === 0) {
            logger.warn(
                {
                    productSku,
                    language,
                    hasTransition: !!transition,
                    hasClose: !!close,
                    hasEnroll: !!enroll,
                    benefitCount: offerBenefits.length,
                },
                "Sales narrative content is incomplete",
            );
            return null;
        }

        return {
            productSku,
            transition,
            guide: toItem(entry.fields.guide as LinkedItem),
            offerBenefits,
            close,
            enroll,
        };
    } catch (error) {
        // error, not warn: this is rethrown rather than recovered, so a Contentful outage should reach
        // on-call rather than sit in warnings.
        logger.error({ error, productSku, language }, "Error fetching sales narrative content");
        throw error;
    }
}
